import React, { useEffect, useRef, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/User';
import './ProductModal.css';
import { Add, DeleteOutlined } from '@mui/icons-material';
import Button from '@mui/material/Button';

function ProductModal({ product, images, onClose, deletePhoto, addPhoto, onDrag }) {
  const modalRef = useRef(null);
  const [fileNames, setFileNames] = useState([]);
  const [nameError, setNameError] = useState(false);
  const [list, setList] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { user } = useContext(UserContext);
  const [isChanged, setIsChanged] = useState(false);

  const isAdmin = user && !user.isAnonymous && user.emailVerified;

  useEffect(() => {
    setList(product.images);
  }, [product, images]);

  const handleCloseModal = (event) => {
    if (!modalRef.current.contains(event.target)) {
      onClose(isChanged, product.id, list);
    }
  };

  const getCalculatorRoute = (productName) => {
    return `/${productName.toLowerCase()}calculator`;
  };

  const startDrag = (e, image) => {
    if (!isAdmin) return;

    setDragging(image);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    e.preventDefault();
  };

  const dragMove = (e) => {
    if (!dragging || !isAdmin) return;

    const dx = e.clientX - dragStartPos.x;
    const dy = e.clientY - dragStartPos.y;

    setDragOffset({ x: dx, y: dy });

    const newHoveredIndex = getNewImageIndex(e.clientX, e.clientY);
    if (newHoveredIndex !== hoveredIndex) {
      setHoveredIndex(newHoveredIndex);
    }
  };

  const endDrag = (e) => {
    if (!dragging || !isAdmin) return;

    const targetIndex = getNewImageIndex(e.clientX, e.clientY);

    if (targetIndex !== -1 && targetIndex !== list.findIndex(i => i === dragging)) {
      rearrangeImages(dragging, targetIndex);
    }

    setDragging(null);
    setDragOffset({ x: 0, y: 0 });
    setHoveredIndex(null);
  };

  const getNewImageIndex = (x, y) => {
    const imageElements = document.querySelectorAll('.image-container');
    let closestIndex = -1;
    let closestDistance = Infinity;

    imageElements.forEach((element, index) => {
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  const rearrangeImages = (image, targetIndex) => {
    const index = list.findIndex((img) => img === image);
    const updatedList = [...list];
    updatedList.splice(index, 1);
    updatedList.splice(targetIndex, 0, image);
    setList(updatedList);
    setIsChanged(true);
    onDrag(product.id, updatedList);
  };

  async function openFilePicker() {
    let options = {
      excludeAcceptAllOption: true,
      id: 'pics',
      multiple: true,
      types: [
        {
          description: "Images",
          accept: {
            "image/*": [".png", ".gif", ".jpeg", ".jpg"],
          },
        },
      ]
    }
    window.showOpenFilePicker(options).then(async data => {
      let names = []
      let isError = false;
      for(let file of data) {
        let image = await file.getFile();
        if(list.includes(image.name)) {
          names.push(image.name);
          isError = true;
        } else {
          setIsChanged(false);
          addPhoto(product.id, image, image.name);
        }
      }
      setFileNames(names);
      setNameError(isError);
      if(!isError) {
        resetFilePick();
      }
    }).catch(err => {
      resetFilePick();
    });
  }

  function resetFilePick() {
    setFileNames([]);
    setNameError(false);
  }

  function deleteImage(name) {
    setIsChanged(false);
    deletePhoto(product.id, name);
  }

  return (
    <div className="modal" onClick={handleCloseModal}>
      <div className="modal-content" ref={modalRef}>
        <div className='title-container'>
          <span className="close-button" onClick={() => onClose(isChanged, product.id, list)}>&times;</span>
          <h2 className="title">{product.name}</h2>
          {isAdmin &&
            <div className='addImage'>
              <Button variant='text' color='primary' onClick={openFilePicker}>
                <Add fontSize='small' />
              </Button>
              {fileNames.length > 0 && fileNames.map((name, idx) => (
                <span key={idx}>{name}</span>
              ))}
              {nameError &&
                <span className='nameError'>
                  **A file already with the same name already exists**
                </span>
              }
            </div>
          }
        </div>
        <div className="image-gallery">
          {list.map((image, index) => (
            <div
              key={index}
              className={`image-container ${hoveredIndex === index ? 'hovered' : ''}`}
              onMouseDown={isAdmin ? (e) => startDrag(e, image) : null}
              onMouseMove={isAdmin ? dragMove : null}
              onMouseUp={isAdmin ? endDrag : null}
              style={{
                position: 'relative',
                cursor: isAdmin ? dragging === image ? 'grabbing' : 'grab' : 'default',
              }}
            >
              <img
                src={images[image]}
                alt={`Product ${index + 1}`}
                style={{
                  transform: dragging === image ? `translate(${dragOffset.x}px, ${dragOffset.y}px)` : 'none',
                  transition: dragging === image ? 'none' : 'transform 0.2s ease',
                }}
              />
              {isAdmin && (
                <div className="image-name">
                  <span>{image}</span>
                  <DeleteOutlined onClick={() => deleteImage(image)} fontSize="small"
                    sx={{cursor: 'pointer'}}  
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <Link to={getCalculatorRoute(product.id)} className="quote-button">Request a Quote</Link>
      </div>
    </div>
  );
}

export default ProductModal;
